import React from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Container, Typography, useMediaQuery, useTheme, Grid, Skeleton, Stack } from "@mui/material";

import Requirements from "./Requirements";
import HeroSection from "../../components/common/HeroSection";
import { getVisaDocumentPage } from "../../services/VisaFilePageService";
import { HeadingText } from "../../utils/CustomTagComponents";

const PrepareVisaFile = () => {
  const { country_id, country_name } = useSelector((state) => state.auth || {});

  const { data: programData, isLoading: loading } = useQuery({
    queryKey: ["prepareVisaFilePage", country_id],
    queryFn: async () => {
      if (!country_id) return null;
      const res = await getVisaDocumentPage(country_id);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      }
      return null;
    },
    enabled: !!country_id,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getResponsiveHtml = (html) => {
    if (!html) return "";
    let clean = html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/gi, " ")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/\[empty_line\]/gi, "<br />");
    if (!isMobile) return clean;
    return clean.replace(/font-weight:\s*900/gi, "font-weight: 700");
  };

  const parseDoDont = (html) => {
    if (!html) return { dos: [], donts: [] };
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const lists = temp.querySelectorAll("ul");
    let dos = [];
    let donts = [];
    lists.forEach((ul, index) => {
      const items = Array.from(ul.querySelectorAll("li")).map(li => li.innerText.trim());
      if (index === 0) dos = items;
      if (index === 1) donts = items;
    });
    return { dos, donts };
  };

  const contentStyles = {
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.625,
    textAlign: "justify",
    "& p": { marginBlockStart: '0px !important', marginBottom: "16px" },
    "& p:last-of-type": { marginBottom: "0px" },
    "& p:has(strong)": { position: "relative", paddingLeft: "20px", marginBottom: "8px", fontWeight: 500 },
    "& p:has(strong)::before": { content: '"\\f101"', fontFamily: "FontAwesome", position: "absolute", left: 0, top: "2px", color: "#fd0000", fontSize: "14px" },
    "& ul": { listStyle: "none", paddingLeft: "0px", marginBottom: "12px" },
    "& li": { position: "relative", paddingLeft: "28px", marginBottom: "4px", lineHeight: 1.7 },
    "& li:first-of-type": { marginTop: "-10px" },
    "& li:last-of-type": { marginBottom: "15px" },
    "& li::before": { content: '"\\f35a"', fontFamily: "FontAwesome", position: "absolute", left: '5px', top: "2px", fontSize: "14px", color: "#fd0000" },
    "& h1, & h2, & h3, & h4": { marginTop: "20px", marginBottom: "12px", fontWeight: 600 },
    "& strong": { fontWeight: 600 },
  };

  // if (loading)
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
  //       <CircularProgress color="warning" />
  //     </Box>
  //   );

  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Grid container spacing={2} alignItems="center">

          {/* Left skeleton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="text" height={60} width="80%" />
            <Skeleton variant="text" height={30} width="90%" />
            <Skeleton variant="text" height={30} width="85%" />

            <Stack direction="row" spacing={2} mt={3}>
              <Skeleton variant="rounded" width={140} height={45} />
              <Skeleton variant="rounded" width={140} height={45} />
            </Stack>

            <Stack spacing={2} mt={3}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="75%" />
            </Stack>
          </Grid>

          {/* Right skeleton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton
              variant="rectangular"
              height={420}
              sx={{ borderRadius: 2 }}
            />
          </Grid>

        </Grid>
      </Container>
    );
  }

  if (!programData)
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <p>No visa document data found for {country_name || "selected country"}.</p>
      </Box>
    );

  const heroTitle = programData.hero_section_title || "";
  const heroSubtitle = programData.hero_section_desc || "";
  const heroImage = programData.hero_section_image || "";
  const heroImageType = programData.hero_section_image_type || "";
  const doc_title = programData.doc_title || "";
  const docs_list = programData.docs_list || [];
  const doc_image = programData.doc_image || "";
  const doc_image_type = programData.doc_image_type || "image";
  const do_dont_title = programData.do_dont_title || "";
  const do_dont_desc = programData.do_dont_desc || "";
  const after_title = programData.after_title || "";
  const after_desc = programData.after_desc || "";

  const { dos, donts } = parseDoDont(do_dont_desc);

  return (
    <>
      <HeroSection
        titleContent={heroTitle}
        subtitleContent={heroSubtitle}
        heroImage={heroImage}
        heroImageType={heroImageType}
      />
      <Requirements title={doc_title} docs_list={docs_list} mediaType={doc_image_type} mediaLink={doc_image} />
      {do_dont_title && do_dont_desc && (
        <Container sx={{ py: { xs: 2, sm: 2, md: 3 }, px: { xs: 3, sm: 4, md: 6 }, paddingLeft: { xs: "24px !important", md: "48px !important" }, paddingRight: { xs: "24px !important", md: "48px !important" } }}>
          <Box sx={{ display: "inline-block", position: "relative", mb: 3, "&::after": { content: '""', position: "absolute", left: 0, bottom: -8, width: "200px", height: "2px", backgroundColor: "#fd0000", opacity: "0.5" } }}>
            <HeadingText mb={1} sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" }, fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(do_dont_title) }} />
          </Box>
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: "#002B5B", color: "#fff", fontWeight: 700 }}>
              <Box sx={{ px: 2, py: 1.5, borderRight: "1px solid #000000" }}>Do's</Box>
              <Box sx={{ px: 2, py: 1.5 }}>Don'ts</Box>
            </Box>
            {dos.map((item, index) => (
              <Box key={index} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid #e5e7eb", background: "#fff" }}>
                <Box sx={{ px: 2, py: 1.5, borderRight: "1px solid #e5e7eb" }}><Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>{item}</Typography></Box>
                <Box sx={{ px: 2, py: 1.5 }}><Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>{donts[index]}</Typography></Box>
              </Box>
            ))}
          </Box>
        </Container>
      )}
      {after_title && after_desc && (
        <Container sx={{ py: { xs: 2, sm: 2, md: 3 }, px: { xs: 3, sm: 4, md: 6 }, paddingLeft: { xs: "24px !important", md: "48px !important" }, paddingRight: { xs: "24px !important", md: "48px !important" } }}>
          <Box sx={{ display: "inline-block", position: "relative", mb: 2, "&::after": { content: '""', position: "absolute", left: 0, bottom: -8, width: "200px", height: "2px", backgroundColor: "#fd0000", opacity: "0.5" } }}>
            <HeadingText mb={1} sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" }, fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(after_title) }} />
          </Box>
          <Box sx={{ borderRadius: "10px", mt: { xs: 2, md: 0 }, width: "100%", zIndex: 2, display: "flex" }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "16px" }, textAlign: "justify", ...contentStyles }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(after_desc) }} />
          </Box>
        </Container>
      )}
    </>
  );
};

export default PrepareVisaFile;
